import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  NgbModal,
  NgbModalModule,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { GenericState } from 'src/app/enums/generic-state.enum';

import { IconsModule } from '../../icons/icons.module';

@Component({
  selector: 'app-action-modal',
  templateUrl: './action-modal.component.html',
  styleUrls: ['./action-modal.component.css'],
  standalone: true,
  imports: [CommonModule, NgbModalModule, IconsModule],
})
export class ActionModalComponent {
  @Input()
  submitButtonClass: string = 'lty-btn-danger';

  @Input()
  disabled = false;

  @Input()
  submitDisabled = false;

  @Input()
  showHeader = false;

  @Input()
  fullWidthFooter = false;

  @Input()
  headerIconState: GenericState;

  _size: 'sm' | 'md' | 'lg' | 'xl' = 'sm';

  @Input()
  set size(value) {
    this._size = value;
    this.updateModalSize();
  }

  get size() {
    return this._size;
  }

  private _customModalClass: string;

  @Input()
  set customModalClass(value: string) {
    this._customModalClass = value;
    this.updateModalClass();
  }

  get customModalClass() {
    return this._customModalClass;
  }

  @Output()
  cancelled = new EventEmitter();

  @Output()
  submitted = new EventEmitter();

  /**
   * Fires once per opening, however the modal went away: the buttons, the cross,
   * the backdrop or Escape. Content projected into the `<ng-template>` below is
   * created with its *parent* view and only moved in and out of the DOM, so a
   * child that must not outlive the opening needs this to know when to go.
   */
  @Output()
  closed = new EventEmitter();

  @ViewChild('actionModal')
  content!: TemplateRef<HTMLElement>;

  modalRef!: NgbModalRef;

  baseClassName = 'action-modal';

  constructor(private readonly modalService: NgbModal) {}

  openModal() {
    this.modalRef = this.modalService.open(this.content, {
      centered: true,
      size: this.size,
      windowClass: `${this.baseClassName} ${this.customModalClass ?? ''}`,
    });
    // `result` resolves when the modal is closed and rejects when it is
    // dismissed; both mean "gone", and handling both also keeps the rejection
    // from surfacing as an unhandled promise.
    this.modalRef.result.then(
      () => this.closed.emit(),
      () => this.closed.emit()
    );
  }

  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  submit() {
    this.submitted.emit();
  }

  cancel() {
    this.cancelled.emit();
    this.closeModal();
  }

  updateModalSize() {
    if (this.modalRef) {
      this.modalRef.update({ size: this.size });
    }
  }

  updateModalClass() {
    if (this.modalRef) {
      this.modalRef.update({
        windowClass: `${this.baseClassName} ${this.customModalClass ?? ''}`,
      });
    }
  }
}
